import { I18nProvider } from "@lingui/react"
import { MantineProvider } from "@mantine/core"
import { ModalsProvider } from "@mantine/modals"
import { Notifications } from "@mantine/notifications"
import { QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { DesktopUpdateGate } from "./components/DesktopUpdateGate"
import { UpdatePrompt } from "./components/UpdatePrompt"
import { RecordingStateProvider } from "./contexts/RecordingStateContext"
import { installGlobalErrorReporting } from "./lib/client-error-reporting"
import { activateLocale, hasUserPickedLocale, i18n, resolveInitialLocale } from "./lib/i18n"
import { resolveAndApplyInitialLexTheme } from "./lib/lex-themes"
import { queryClient } from "./lib/query-client"
import { requestReload } from "./lib/recording-guard"
import { IS_TAURI } from "./lib/runtime-origin"
import { detectProduct } from "./lib/tenant"
import { applyTheme, resolveInitialTheme } from "./lib/theme-mode"
import { router } from "./router"
import { getTheme, lexicanonCssVariables, voxcanonCssVariables } from "./theme"

import "./styles/lexicanon-tokens.css"
import "./styles/voxcanon-tokens.css"
import "@mantine/core/styles.css"
import "@mantine/notifications/styles.css"
import "./app.css"

// When a new deployment invalidates old Vite asset hashes, any lazy-loaded
// chunk that the browser tries to fetch will 404. Vite fires this event so
// we can reload cleanly instead of leaving the user on a broken page — but never
// mid-recording (requestReload defers until the recording ends).
window.addEventListener("vite:preloadError", () => {
  requestReload()
})

// Capture uncaught errors + unhandled rejections from first paint. They only
// relay once a tenant is active (set in __root); earlier ones console.error.
installGlobalErrorReporting()

const product = detectProduct()
document.documentElement.setAttribute("data-product", product)

// Desktop is a thin shell that loads the hosted SPA fresh on each launch, so the
// PWA service worker has no purpose here — it only adds a stale asset cache and a
// "update now" prompt whose SW-reload doesn't work in the Tauri webview. Neutralise
// it in the desktop shell; the web build keeps its PWA. Registration happens in
// exactly one place — <UpdatePrompt>, which is mounted web-only — so this loop is
// legacy cleanup for SWs registered by older builds, not a counter to anything
// active. (IndexedDB — meeting recovery — is untouched.)
if (IS_TAURI && "serviceWorker" in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .catch(() => undefined)
  if (typeof caches !== "undefined") {
    caches
      .keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => undefined)
  }
}

// Voxcanon is dual-theme — pick a surface before first paint to avoid a
// flash. Lexicanon now also accepts data-theme (currently the "bordeaux"
// editorial variant for professional-services tenants) — keep both flows
// pre-paint so the user doesn't see a flash from default-to-themed.
if (product === "voxcanon") {
  applyTheme(resolveInitialTheme())
}

// Lexicanon themes — Pastel Calm is the global default (2026-05-30). Pre-paint
// we resolve a `?theme=` transient preview, else the cached per-device theme,
// else Pastel — and inject the matching webfont. The COMPANY default is applied
// once tenant branding resolves (routes/__root.tsx), and a signed-in user's
// saved override is applied post-auth (routes/_auth.tsx). The full resolution
// order + helpers live in lib/lex-themes.ts.
if (product === "lexicanon") {
  resolveAndApplyInitialLexTheme(window.location.search)
}

// Set page title based on product
document.title = product === "voxcanon" ? "Voxcanon" : "Lexicanon"

// Voxcanon owns its own font set (Cinzel + Nunito); Lexicanon's per-theme fonts
// are injected by applyLexTheme above.
if (product === "voxcanon") {
  const fontLink = document.createElement("link")
  fontLink.rel = "stylesheet"
  fontLink.href =
    "https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=Nunito:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap"
  document.head.appendChild(fontLink)
}

const rootElement = document.getElementById("root")
if (!rootElement) throw new Error("Root element not found")

// Activate the resolved locale BEFORE first render so the initial paint is
// already in the user's language. activateLocale dynamic-imports the catalog
// so the cost is one extra HTTP request before the app shell appears.
const initialLocale = resolveInitialLocale()
// Only persist when this device already has an explicit pick. Writing the
// browser fallback used to block company `default_ui_locale` forever.
activateLocale(initialLocale, { persist: hasUserPickedLocale() }).then(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <I18nProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <MantineProvider
            theme={getTheme(product)}
            // Lexicanon defaults to the light Bordeaux & Cream scheme (the
            // editorial brand look); users can still toggle dark. Voxcanon is
            // always dark via forceColorScheme below.
            defaultColorScheme="light"
            forceColorScheme={product === "voxcanon" ? "dark" : undefined}
            cssVariablesResolver={product === "voxcanon" ? voxcanonCssVariables : lexicanonCssVariables}
          >
            <RecordingStateProvider>
              {/* PWA update prompt is web-only — the desktop shell loads fresh on
                  reopen and uses the native updater (DesktopUpdateGate) instead. */}
              {!IS_TAURI && <UpdatePrompt />}
              <DesktopUpdateGate />
              <Notifications position="top-right" />
              <ModalsProvider>
                <RouterProvider router={router} />
              </ModalsProvider>
            </RecordingStateProvider>
          </MantineProvider>
        </QueryClientProvider>
      </I18nProvider>
    </StrictMode>,
  )
})
